import { useEffect, useMemo, useState } from 'react';
import { CustomTypography } from '../Typography/Typography';
import { TextInput } from '../TextInput/TextInput';
import { useStyles } from './CustomReturnedConcreteModal.styles';
import { IDosagePropertyEntity } from 'types';
import { IDosageProperty } from 'types';

interface IDosageValue {
  dosageTemperaturePropertyModel: IDosageProperty;
  dosageBatchAgePropertyModel: IDosageProperty;
  value: number;
}

interface IEditableDosageTableProps {
  values: IDosageValue[];
  properties: IDosagePropertyEntity[];
  valueUom?: string;
  isMetric?: boolean;
  cementOnly?: boolean;
  canEdit: boolean;
  isLoading: boolean;
  onCreate: (payload: {
    dosageTemperaturePropertyId: string;
    dosageBatchAgePropertyId: string;
    value: number;
  }) => Promise<void>;
  onUpdate: (payload: {
    dosageTemperaturePropertyId: string;
    dosageBatchAgePropertyId: string;
    value: number;
  }) => Promise<void>;
  onDelete: (payload: {
    dosageTemperaturePropertyId: string;
    dosageBatchAgePropertyId: string;
    value: number;
  }) => Promise<void>;
}

const formatRange = (property: IDosagePropertyEntity | IDosageProperty) =>
  `${property.dosagePropertyRange}${property.uom ? ` ${property.uom}` : ''}`;

const formatName = (name: string) => {
  const formatted = name.replace(/([a-z])([A-Z])/g, '$1 $2');
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const TEMPERATURE_UOM = { metric: '°C', imperial: '°F' };
const BATCH_AGE_UOM = 'min';
const DOSAGE_VALUE_UOM = {
  cementOnly: { metric: 'mL/100 kg', imperial: 'oz/cwt' },
  perVolume: { metric: 'mL/m³', imperial: 'oz/yd³' },
};

export const EditableDosageTable = ({
  values,
  properties,
  valueUom,
  isMetric,
  cementOnly,
  canEdit,
  isLoading,
  onCreate,
  onUpdate,
  onDelete,
}: IEditableDosageTableProps) => {
  const { classes } = useStyles();
  const [localValues, setLocalValues] = useState(values);
  const [editing, setEditing] = useState<string>();
  const [draftValue, setDraftValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const temperatures = useMemo(
    () =>
      properties
        .filter(property => property.dosagePropertyName === 'temperature')
        .sort((a, b) => a.rangeMin - b.rangeMin),
    [properties],
  );
  const batchAges = useMemo(
    () =>
      properties
        .filter(property => property.dosagePropertyName === 'batchAge')
        .sort((a, b) => a.rangeMin - b.rangeMin),
    [properties],
  );

  const temperatureUom =
    temperatures[0]?.uom ||
    (isMetric ? TEMPERATURE_UOM.metric : TEMPERATURE_UOM.imperial);
  const batchAgeUom = batchAges[0]?.uom || BATCH_AGE_UOM;
  const dosageUnits = cementOnly
    ? DOSAGE_VALUE_UOM.cementOnly
    : DOSAGE_VALUE_UOM.perVolume;
  const resolvedValueUom =
    valueUom || (isMetric ? dosageUnits.metric : dosageUnits.imperial);

  const getValue = (temperatureId: string, batchAgeId: string) =>
    localValues.find(
      value =>
        value.dosageTemperaturePropertyModel.dosagePropertyId ===
          temperatureId &&
        value.dosageBatchAgePropertyModel.dosagePropertyId === batchAgeId,
    );

  const startEditing = (temperatureId: string, batchAgeId: string) => {
    if (!canEdit || isLoading) return;
    const value = getValue(temperatureId, batchAgeId);
    setEditing(`${temperatureId}:${batchAgeId}`);
    setDraftValue(value ? value.value.toString() : '');
  };

  const save = async (temperatureId: string, batchAgeId: string) => {
    const savedCellKey = `${temperatureId}:${batchAgeId}`;
    const trimmedValue = draftValue.trim();
    const existing = getValue(temperatureId, batchAgeId);
    if (!trimmedValue && existing && !saving && !isLoading) {
      setSaving(true);
      try {
        await onDelete({
          dosageTemperaturePropertyId: temperatureId,
          dosageBatchAgePropertyId: batchAgeId,
          value: existing.value,
        });
        setLocalValues(currentValues =>
          currentValues.filter(
            currentValue =>
              currentValue.dosageTemperaturePropertyModel.dosagePropertyId !==
                temperatureId ||
              currentValue.dosageBatchAgePropertyModel.dosagePropertyId !==
                batchAgeId,
          ),
        );
      } finally {
        setSaving(false);
        setEditing(currentEditing =>
          currentEditing === savedCellKey ? undefined : currentEditing,
        );
      }
      return;
    }

    if (!trimmedValue || saving || isLoading) {
      setEditing(currentEditing =>
        currentEditing === savedCellKey ? undefined : currentEditing,
      );
      return;
    }

    const value = Number(trimmedValue);
    if (!Number.isFinite(value)) {
      setEditing(currentEditing =>
        currentEditing === savedCellKey ? undefined : currentEditing,
      );
      return;
    }

    const payload = {
      dosageTemperaturePropertyId: temperatureId,
      dosageBatchAgePropertyId: batchAgeId,
      value,
    };
    setSaving(true);
    try {
      if (existing) {
        await onUpdate(payload);
        setLocalValues(currentValues =>
          currentValues.map(currentValue =>
            currentValue.dosageTemperaturePropertyModel.dosagePropertyId ===
              temperatureId &&
            currentValue.dosageBatchAgePropertyModel.dosagePropertyId ===
              batchAgeId
              ? { ...currentValue, value }
              : currentValue,
          ),
        );
      } else {
        await onCreate(payload);
        const temperature = temperatures.find(
          property => property.dosagePropertyId === temperatureId,
        );
        const batchAge = batchAges.find(
          property => property.dosagePropertyId === batchAgeId,
        );
        if (temperature && batchAge) {
          setLocalValues(currentValues => [
            ...currentValues,
            {
              dosageTemperaturePropertyModel: temperature,
              dosageBatchAgePropertyModel: batchAge,
              value,
            },
          ]);
        }
      }
    } finally {
      setSaving(false);
      setEditing(currentEditing =>
        currentEditing === savedCellKey ? undefined : currentEditing,
      );
    }
  };

  return (
    <div className={classes.editableDosageTableContainer}>
      <div className={classes.editableDosageTableActions}>
        {canEdit && (
          <CustomTypography variant="caption2" color="accentDark">
            Click a cell to edit or add a dosage value.
          </CustomTypography>
        )}
        <CustomTypography variant="caption2" color="accentDark">
          {`Dosage values in ${resolvedValueUom}`}
        </CustomTypography>
      </div>
      <table className={classes.editableDosageTable}>
        <thead>
          <tr>
            <th className={classes.editableDosageCorner}>
              <span>
                {`${formatName(
                  temperatures[0]?.dosagePropertyName || 'temperature',
                )} (${temperatureUom})`}
              </span>
              <span>
                {`${formatName(
                  batchAges[0]?.dosagePropertyName || 'batchAge',
                )} (${batchAgeUom})`}
              </span>
            </th>
            {batchAges.map(batchAge => (
              <th key={batchAge.dosagePropertyId}>{formatRange(batchAge)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {temperatures.map(temperature => (
            <tr key={temperature.dosagePropertyId}>
              <th>{formatRange(temperature)}</th>
              {batchAges.map(batchAge => {
                const key = `${temperature.dosagePropertyId}:${batchAge.dosagePropertyId}`;
                const existing = getValue(
                  temperature.dosagePropertyId,
                  batchAge.dosagePropertyId,
                );
                const isEditing = editing === key;
                return (
                  <td key={key}>
                    {isEditing ? (
                      <div className={classes.editableDosageCell}>
                        <TextInput
                          name={key}
                          currentValue={draftValue}
                          backgroundColor="white"
                          autoFocus
                          onChangeHandler={event =>
                            setDraftValue(event?.target?.value || '')
                          }
                          onBlur={() =>
                            save(
                              temperature.dosagePropertyId,
                              batchAge.dosagePropertyId,
                            )
                          }
                        />
                        {resolvedValueUom && (
                          <span className={classes.editableDosageValueUom}>
                            {resolvedValueUom}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`${classes.editableDosageCellButton} ${
                          existing ? classes.editableDosageCellFilled : ''
                        }`}
                        onClick={() =>
                          startEditing(
                            temperature.dosagePropertyId,
                            batchAge.dosagePropertyId,
                          )
                        }
                      >
                        {existing
                          ? `${existing.value}${
                              resolvedValueUom ? ` ${resolvedValueUom}` : ''
                            }`
                          : ''}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
